import { useEffect, useState } from 'react'
import type { Person } from '@cvut/profit-api-types/lib/theses'
import { Role } from '@cvut/profit-theses-common'

import * as Button from '../../components/Button.style'
import Card from '../../components/Card'
import PeopleSearchCard from '../../components/PeopleSearchCard'
import PersonCard from '../../components/PersonCard'
import PageHeader from '../../components/PageHeader'
import PageLoadSpinner from '../../components/PageLoadSpinner'
import RequestDependentRender from '../../api/RequestDependentRender'
import { errorCardRenderer } from '../../components/RequestErrorCard'
import { useAllSpecializations } from '../../api/specializations'
import { useLocale } from '../../locale'
import { formatPersonFullName } from '../../utils/person'
import pagePaths from '../paths'
import { getPerson, patchPerson } from '../../api/people'
import { addNotification } from '../../features/toastNotifications'
import MultiSelect, { Option } from '../../components/form/MultiSelect'


const SpecializationOfficersPage = (): JSX.Element => {
  const { l } = useLocale()
  const [specializationsState, getAllSpecializations] = useAllSpecializations()

  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null)
  const [selectedSpecializations, setSelectedSpecializations] = useState<Option[]>([])
  const [officerSpecializations, setOfficerSpecializations] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [lastModified, setLastModified] = useState<string | undefined>(undefined)

  useEffect(() => {
    void getAllSpecializations()
  }, [])

  // Derive officer specializations from roles of the selected person
  useEffect(() => {
    if (selectedPerson) {
      const prefix = `${Role.SPEC_OFFICER_}-`
      const specOfficerRoles = selectedPerson.roles?.filter((role: string) => role.startsWith(prefix)) || []
      setOfficerSpecializations(specOfficerRoles.map((role: string) => role.substring(prefix.length)))
    } else {
      setOfficerSpecializations([])
    }
  }, [selectedPerson])

  return (
    <article>
      <PageHeader title={l.siteHeader.specializationOfficers} />
      <div style={{ marginBottom: '20px' }}>
        <PeopleSearchCard
          title={l.siteHeader.specializationOfficers}
          placeholder={l.pages.specializationOfficers.searchPlaceholder}
          onPersonSelect={async (person: Person) => {
            try {
              const [resp, fullPerson] = await getPerson(person.username)
              setSelectedPerson(fullPerson)
              setLastModified(resp.headers.get('Last-Modified') ?? undefined)
            } catch {
              addNotification({ type: 'NEGATIVE', message: l.errorMessages.api.errorGettingData })
            }
          }}
          showAllPeopleInitially
        />
      </div>
      {selectedPerson && (
        <div style={{ marginBottom: '20px' }}>
          <Card>
            <Card.Header title={l.pages.specializationOfficers.selectedPerson} />
            <Card.Content>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <PersonCard
                    name={formatPersonFullName(selectedPerson)}
                    contact={selectedPerson.email}
                  />
                  {selectedPerson.roles && selectedPerson.roles.length > 0 && (
                    <div style={{ marginTop: '16px' }}>
                      <strong>{l.pages.specializationOfficers.currentRoles}</strong>
                      <ul style={{ marginTop: '8px' }}>
                        {selectedPerson.roles.map((role, index) => (
                          <li key={index}>{role}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <button
                  type='button'
                  onClick={() => setSelectedPerson(null)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  {l.pages.specializationOfficers.clearSelection}
                </button>
              </div>
            </Card.Content>
          </Card>
        </div>
      )}
      {selectedPerson && (
        <RequestDependentRender
          requestStatus={specializationsState}
          renderOnLoading={() => <PageLoadSpinner message={l.specialization.loadingSpecializations} />}
          renderOnError={errorCardRenderer(
            l.specialization.errorLoadingSpecializations,
            pagePaths.specializationOfficers,
          )}
          renderOnSuccess={(specializations) => {
            // Show current officer roles
            const officerSpecs = specializations.filter(s => officerSpecializations.includes(s.code))
            const availableSpecs = specializations.filter(s => !officerSpecializations.includes(s.code))

            return (
              <div style={{ marginBottom: '20px' }}>
                <label>{l.specialization.specialization}</label>
                <div>
                  <strong>{l.pages.specializationOfficers.currentOfficerRoles}</strong>
                  {officerSpecs.length > 0 ? (
                    <ul>
                      {officerSpecs.map(s => (
                        <li key={s.code} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span>{s.nameCs} ({s.code})</span>
                          <button
                            type='button'
                            disabled={isSubmitting}
                            onClick={async () => {
                              if (!selectedPerson) return
                              setIsSubmitting(true)
                              try {
                                const prefix = `${Role.SPEC_OFFICER_}-`
                                const roleToRemove = `${prefix}${s.code}`
                                const updatedRoles = (selectedPerson.roles ?? []).filter(r => r !== roleToRemove)
                                await patchPerson(selectedPerson.username, { roles: updatedRoles }, {
                                  ifUnmodifiedSince: lastModified,
                                })
                                const [resp, refreshed] = await getPerson(selectedPerson.username)
                                setSelectedPerson(refreshed)
                                setLastModified(resp.headers.get('Last-Modified') ?? undefined)
                                addNotification({ type: 'POSITIVE', message: 'Officer role removed' })
                              } catch (err) {
                                addNotification({ type: 'NEGATIVE', message: 'Failed to remove officer role' })
                              } finally {
                                setIsSubmitting(false)
                              }
                            }}
                            style={{
                              padding: '4px 8px',
                              border: '1px solid #ddd',
                              borderRadius: 4,
                              background: '#fff',
                              cursor: 'pointer',
                            }}
                          >{l.pages.specializationOfficers.removeButton}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span>{l.pages.specializationOfficers.noOfficerRolesAssigned}</span>
                  )}
                </div>
                <div style={{ marginTop: '16px' }}>
                  <label>{l.pages.specializationOfficers.addOfficerRoles}</label>
                  <MultiSelect
                    options={availableSpecs.map(s => ({ label: `${s.nameCs} (${s.code})`, value: s.code }))}
                    onChange={(selected: Option[]) => setSelectedSpecializations(selected || [])}
                    isMulti
                    placeholder={l.pages.specializationOfficers.selectSpecializationsPlaceholder}
                  />
                  <button
                    className={Button.fill}
                    disabled={selectedSpecializations.length === 0 || isSubmitting}
                    onClick={async () => {
                      if (!selectedPerson || selectedSpecializations.length === 0) return
                      setIsSubmitting(true)
                      try {
                        const prefix = `${Role.SPEC_OFFICER_}-`
                        const newRoles = selectedSpecializations.map(spec => `${prefix}${spec.value}`)
                        const updatedRoles = Array.from(new Set([...(selectedPerson?.roles ?? []), ...newRoles]))

                        await patchPerson(selectedPerson.username, { roles: updatedRoles }, {
                          ifUnmodifiedSince: lastModified,
                        })

                        const [resp, refreshed] = await getPerson(selectedPerson.username)
                        setSelectedPerson(refreshed)
                        setLastModified(resp.headers.get('Last-Modified') ?? undefined)
                        setSelectedSpecializations([])
                        const count = newRoles.length
                        const plural = count > 1 ? 's' : ''
                        addNotification({
                          type: 'POSITIVE',
                          message: l.pages.specializationOfficers.addedOfficerRoles
                            .replace('{count}', String(count))
                            .replace('{plural}', plural),
                        })
                      } catch (err) {
                        addNotification({
                          type: 'NEGATIVE',
                          message: l.pages.specializationOfficers.failedToAddOfficerRoles,
                        })
                      } finally {
                        setIsSubmitting(false)
                      }
                    }}
                  >{isSubmitting ? 'Submitting...' : l.form.submit}
                  </button>
                </div>
              </div>
            )
          }}
        />
      )}
      {/* Submit button moved to inside specialization selection */}
    </article>
  )
}

export default SpecializationOfficersPage
